/**
 * Central GA4 analytics service.
 *
 * All tracking goes through this module -- UI components should never call
 * `gtag()` directly.  This gives us a single place to:
 *   - gate on consent & initialization
 *   - deduplicate events
 *   - sanitize parameters (strip PII)
 *   - swap provider later without touching 30 files
 *
 * We load the gtag script with consent-mode defaults so that, if a cookie
 * banner is added later, the transition is seamless.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** GA4 recommended event names we use, plus our custom events. */
type GA4Event =
  // Recommended
  | 'page_view'
  | 'sign_up'
  | 'login'
  | 'view_item_list'
  | 'view_item'
  | 'select_item'
  | 'begin_checkout'
  | 'purchase'
  // Custom
  | 'file_upload'
  | 'quote_ready'
  | 'quote_error'
  | 'material_select'
  | 'color_select'
  | 'checkout_cancel'
  | 'checkout_error'
  | 'outbound_click'
  | 'model_interact';

type EventParams = Record<string, string | number | boolean | undefined>;

interface UserProperties {
  readonly user_type?: 'anonymous' | 'authenticated' | 'admin';
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let initialized = false;
let measurementId = '';

// Dedup guard: tracks the last event+serialized-params to prevent double-fires
// (e.g. strict-mode double-mounts, rapid re-renders).
let lastEventKey = '';

const PII_PATTERNS = [
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,   // email
  /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g,                       // US phone
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip any values that look like PII from event params. */
const sanitize = (params: EventParams): EventParams => {
  const clean: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (typeof value === 'string') {
      let sanitized = value;
      for (const pattern of PII_PATTERNS) {
        sanitized = sanitized.replace(pattern, '[REDACTED]');
      }
      clean[key] = sanitized;
    } else {
      clean[key] = value;
    }
  }
  return clean;
};

/** Access the global gtag function (added by the loader script). */
const gtag = (...args: unknown[]): void => {
  // gtag pushes onto dataLayer; if it doesn't exist yet, queue silently
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.gtag as (...a: any[]) => void)(...args);
  }
};

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Load the GA4 script and initialize with consent-mode defaults.
 * Call once at app startup (e.g. in AnalyticsProvider).
 */
export const initAnalytics = (gaId: string): void => {
  if (initialized || !gaId || typeof window === 'undefined') return;

  measurementId = gaId;

  // 1. Set consent defaults BEFORE loading the script.
  //    Consent Mode v2: default-deny for GDPR compliance.
  //    The cookie consent banner calls `updateConsent()` when the user accepts.
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  // 2. Load the gtag.js script asynchronously (non-blocking).
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  script.async = true;
  document.head.appendChild(script);

  // 3. Initialize GA4.
  //    send_page_view: false -- we fire page_view ourselves on route changes
  //    so SPA navigations are tracked correctly.
  gtag('js', new Date());
  gtag('config', gaId, {
    send_page_view: false,
  });

  initialized = true;
};

/**
 * Update consent state at runtime.  Called by the cookie consent banner.
 * Consent Mode v2 requires all four signals to be set explicitly.
 */
export const updateConsent = (granted: boolean): void => {
  const state = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
};

/** Read persisted consent from localStorage (used on init). */
export const restoreConsent = async (): Promise<void> => {
  try {
    const { secureGetItem } = await import('@/utils/secure-storage');
    const stored = await secureGetItem(localStorage, 'cookie_consent');
    if (stored === 'accepted') {
      updateConsent(true);
    }
    // If 'declined' or absent, defaults (denied) stay in effect.
  } catch {
    // Storage unavailable
  }
};

// ---------------------------------------------------------------------------
// Core tracking
// ---------------------------------------------------------------------------

/**
 * Send a GA4 event.  All event tracking goes through here.
 *
 * Deduplicates identical back-to-back calls (within ~50ms, which covers
 * React strict-mode double-mounts and rapid re-renders).
 */
export const trackEvent = (name: GA4Event, params: EventParams = {}): void => {
  if (!initialized) return;

  const cleaned = sanitize(params);
  const eventKey = `${name}:${JSON.stringify(cleaned)}`;

  // Dedup: skip if identical to the last event fired
  if (eventKey === lastEventKey) return;
  lastEventKey = eventKey;
  // Reset dedup window after a short delay so the same event can fire again
  // on a genuinely new user action.
  setTimeout(() => {
    if (lastEventKey === eventKey) lastEventKey = '';
  }, 500);

  gtag('event', name, cleaned);
};

// ---------------------------------------------------------------------------
// Page views (SPA-aware)
// ---------------------------------------------------------------------------

let lastTrackedPath = '';

/**
 * Track a page view.  Call on every route change.
 * Deduplicates consecutive calls to the same path (e.g. re-renders).
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!initialized) return;
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;

  gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.origin + path,
  });
};

// ---------------------------------------------------------------------------
// User properties (non-PII)
// ---------------------------------------------------------------------------

/**
 * Set user properties.  Call when auth state changes.
 * Never send email, name, or other PII.
 */
export const setUserProperties = (props: UserProperties): void => {
  if (!initialized) return;
  gtag('set', 'user_properties', props);
};

/**
 * Set the GA4 user ID.  Accepts only the opaque Cognito ID -- never email.
 */
export const setUserId = (userId: string | null): void => {
  if (!initialized) return;
  gtag('config', measurementId, {
    user_id: userId ?? undefined,
    send_page_view: false,
  });
};

// ---------------------------------------------------------------------------
// Convenience helpers — typed wrappers for specific events
// ---------------------------------------------------------------------------

// -- Auth events --

export const trackSignUp = (method: string = 'email'): void =>
  trackEvent('sign_up', { method });

export const trackLogin = (method: string = 'email'): void =>
  trackEvent('login', { method });

// -- Quote funnel --

export const trackFileUpload = (params: {
  file_type: string;
  file_size_mb: number;
}): void => trackEvent('file_upload', params);

export const trackQuoteReady = (params: {
  quote_id: string;
  volume_cm3: number;
  material_count: number;
}): void => trackEvent('quote_ready', params);

export const trackQuoteError = (params: {
  quote_id?: string;
  error_type: string;
}): void => trackEvent('quote_error', params);

export const trackMaterialSelect = (params: {
  material_id: string;
  material_name: string;
}): void => trackEvent('material_select', params);

export const trackColorSelect = (params: {
  material_id: string;
  color: string;
}): void => trackEvent('color_select', params);

// -- Ecommerce / checkout --

export const trackViewItemList = (params: {
  item_list_name: string;
  items: ReadonlyArray<{
    item_id: string;
    item_name: string;
    item_category?: string;
    price?: number;
  }>;
}): void => trackEvent('view_item_list', {
  item_list_name: params.item_list_name,
  items: JSON.stringify(params.items),
});

export const trackViewItem = (params: {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
}): void => trackEvent('view_item', params);

export const trackSelectItem = (params: {
  item_list_name: string;
  item_id: string;
  item_name: string;
}): void => trackEvent('select_item', params);

export const trackBeginCheckout = (params: {
  value: number;
  currency?: string;
  checkout_type: 'quote' | 'product';
  material_id?: string;
  color?: string;
  quantity: number;
}): void => trackEvent('begin_checkout', {
  ...params,
  currency: params.currency ?? 'USD',
});

export const trackPurchase = (params: {
  transaction_id: string;
  value?: number;
  currency?: string;
}): void => trackEvent('purchase', {
  ...params,
  currency: params.currency ?? 'USD',
});

export const trackCheckoutCancel = (): void =>
  trackEvent('checkout_cancel', {});

export const trackCheckoutError = (params: {
  error_message: string;
  checkout_type: 'quote' | 'product';
  value?: number;
}): void => trackEvent('checkout_error', params);

// -- Outbound links --

export const trackOutboundClick = (url: string, link_text?: string): void =>
  trackEvent('outbound_click', {
    link_url: url,
    link_text: link_text ?? '',
  });

// -- 3D viewer interactions --

export const trackModelInteract = (
  action: 'screenshot' | 'fullscreen' | 'wireframe' | 'view_preset',
): void => trackEvent('model_interact', { action });

// ---------------------------------------------------------------------------
// Checkout value persistence (sessionStorage)
// ---------------------------------------------------------------------------

const CHECKOUT_VALUE_KEY = 'checkout_value';

/**
 * Stash the checkout value before redirecting to Stripe so the success page
 * can include it in the purchase event.
 */
export const stashCheckoutValue = async (value: number): Promise<void> => {
  try {
    const { secureSetItem } = await import('@/utils/secure-storage');
    await secureSetItem(sessionStorage, CHECKOUT_VALUE_KEY, String(value));
  } catch {
    // sessionStorage unavailable
  }
};

/** Retrieve and clear the stashed checkout value. */
export const popCheckoutValue = async (): Promise<number | undefined> => {
  try {
    const { secureGetItem, secureRemoveItem } = await import('@/utils/secure-storage');
    const raw = await secureGetItem(sessionStorage, CHECKOUT_VALUE_KEY);
    secureRemoveItem(sessionStorage, CHECKOUT_VALUE_KEY);
    if (raw) return parseFloat(raw);
  } catch {
    // sessionStorage unavailable
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Global type augmentation for window.gtag / window.dataLayer
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
  }
}
